// Camera.jsx

import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useUser } from 'context/UserContext';
import { useBase } from 'context/BaseContext';

// Utilities
import { generateKey } from 'utilities/Keys';
import { getDefaultNode } from 'utilities/NodeFactory';
import { buildUserSummary } from 'utilities/Users';

// CSS
import './Camera.css';

// Icons
import Icon from 'svg/Icon';

// Components
import RecordingTimer from './recordingtimer/RecordingTimer';

export const CAMERA_MODES = Object.freeze({
  PHOTO: 'PHOTO',
  VIDEO: 'VIDEO',
});

/**
 * Maps legacy SNAPSHOT/RECORDING contentType values to camera modes.
 *
 * @param {string} contentType - Caller-provided mode hint.
 * @returns {string} PHOTO or VIDEO.
 */
export function resolveCameraMode(contentType) {
  const normalized = String(contentType || '').trim().toUpperCase();
  if (normalized === 'RECORDING' || normalized === 'VIDEO') {
    return CAMERA_MODES.VIDEO;
  }
  return CAMERA_MODES.PHOTO;
}

/**
 * Builds a File (or Blob with name/type) from a capture blob.
 *
 * @param {Blob} blob - Captured media blob.
 * @param {string} fallbackName - Filename fallback.
 * @param {string} mimeType - MIME type fallback.
 * @returns {File|Blob} Uploadable file-like object.
 */
function buildLocalFile(blob, fallbackName, mimeType) {
  let localFile;
  if (typeof File === 'function') {
    try {
      localFile = new File([blob], fallbackName, { type: mimeType });
    } catch {
      localFile = blob;
    }
  } else {
    localFile = blob;
  }

  if (!(localFile instanceof File)) {
    try {
      Object.defineProperty(localFile, 'name', {
        value: fallbackName,
        configurable: true,
      });
    } catch {
      // ignore
    }
    try {
      Object.defineProperty(localFile, 'type', {
        value: mimeType,
        configurable: true,
      });
    } catch {
      // ignore
    }
  }

  return localFile;
}

/**
 * Camera
 *
 * Unified camera surface for photo capture and video recording. Mode is chosen
 * inside the UI (Photo / Video), matching typical camera apps.
 *
 * @param {string} [contentType] - Optional initial mode: SNAPSHOT/PHOTO or RECORDING/VIDEO.
 * @param {function} onContentCreated - Called with the captured media payload.
 *
 * @returns {ReactNode} The rendered camera UI.
 */
const Camera = ({ contentType, onContentCreated }) => {

  // ----- CONTEXT ----- //

  const { user } = useUser();
  const { activeBase } = useBase();

  // ----- STATE ----- //

  const [mode, setMode] = useState(() => resolveCameraMode(contentType));
  const [isRecording, setIsRecording] = useState(false);
  const [cameras, setCameras] = useState([]);
  const [selectedCameraId, setSelectedCameraId] = useState(null);
  const [permissionError, setPermissionError] = useState('');
  const [isStarting, setIsStarting] = useState(true);
  const [flashVisible, setFlashVisible] = useState(false);

  // ----- REFS ----- //

  const chunksRef = useRef([]);
  const mediaStreamRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const videoRef = useRef(null);
  const shutterSoundRef = useRef(null);
  const isRecordingRef = useRef(false);

  // ----- EFFECTS ----- //

  useEffect(() => {
    isRecordingRef.current = isRecording;
  }, [isRecording]);

  useEffect(() => {
    if (isRecording) return;
    setMode(resolveCameraMode(contentType));
  }, [contentType, isRecording]);

  /**
   * Enumerates available video input devices.
   */
  const enumerateCameras = useCallback(async () => {
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.enumerateDevices) {
      return;
    }

    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter((device) => device.kind === 'videoinput');
      setCameras(videoDevices);

      if (videoDevices.length === 0) {
        setSelectedCameraId(null);
        return;
      }

      const activeTrackDeviceId = mediaStreamRef.current
        ?.getVideoTracks()?.[0]
        ?.getSettings()?.deviceId;

      setSelectedCameraId((prev) => {
        if (prev && videoDevices.some((device) => device.deviceId === prev)) {
          return prev;
        }

        if (activeTrackDeviceId && videoDevices.some((device) => device.deviceId === activeTrackDeviceId)) {
          return activeTrackDeviceId;
        }

        return videoDevices[0].deviceId;
      });
    } catch (error) {
      console.error('Camera enumeration failed:', error);
    }
  }, []);

  /**
   * Starts (or restarts) the camera preview stream.
   *
   * @param {string|null} deviceId - Optional device id.
   */
  const startCamera = useCallback(async (deviceId = null) => {
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      setPermissionError('Camera is not supported in this browser.');
      setIsStarting(false);
      return;
    }

    if (isRecordingRef.current) {
      return;
    }

    setPermissionError('');
    setIsStarting(true);

    mediaStreamRef.current?.getTracks().forEach((track) => track.stop());

    try {
      const constraints = {
        video: deviceId
          ? { deviceId: { exact: deviceId } }
          : { facingMode: 'user' },
        audio: true,
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      mediaStreamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }

      const activeDeviceId = stream.getVideoTracks()[0]?.getSettings()?.deviceId || null;
      if (activeDeviceId) {
        setSelectedCameraId((prev) => (prev === activeDeviceId ? prev : activeDeviceId));
      }

      await enumerateCameras();
      setIsStarting(false);
    } catch (error) {
      console.error('Camera access failed:', error);
      setPermissionError(
        error?.name === 'NotAllowedError'
          ? 'Camera access was blocked. Allow camera (and mic for video) to continue.'
          : 'Could not start the camera. Check permissions and try again.',
      );
      setIsStarting(false);
    }
  }, [enumerateCameras]);

  /**
   * Stops all camera tracks and any active recorder.
   */
  const stopCamera = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== 'inactive') {
      try {
        recorder.stop();
      } catch {
        // ignore
      }
    }

    mediaStreamRef.current?.getTracks().forEach((track) => track.stop());
    mediaStreamRef.current = null;
    mediaRecorderRef.current = null;
  }, []);

  useEffect(() => {
    startCamera();
    return () => {
      stopCamera();
    };
  }, [startCamera, stopCamera]);

  useEffect(() => {
    const mediaDevices = typeof navigator !== 'undefined' ? navigator.mediaDevices : null;
    if (!mediaDevices?.addEventListener) return undefined;

    const handleDeviceChange = () => {
      enumerateCameras();
    };

    mediaDevices.addEventListener('devicechange', handleDeviceChange);
    return () => {
      mediaDevices.removeEventListener('devicechange', handleDeviceChange);
    };
  }, [enumerateCameras]);

  // ----- HANDLERS ----- //

  /**
   * Switches Photo / Video mode when not recording.
   *
   * @param {string} nextMode - PHOTO or VIDEO.
   */
  const handleModeChange = (nextMode) => {
    if (isRecording || nextMode === mode) return;
    setMode(nextMode);
  };

  /**
   * Cycles to the next available camera device.
   */
  const handleFlipCamera = async () => {
    if (isRecording || cameras.length < 2) return;

    const currentIndex = cameras.findIndex((camera) => camera.deviceId === selectedCameraId);
    const nextIndex = currentIndex >= 0 ? (currentIndex + 1) % cameras.length : 0;
    const nextId = cameras[nextIndex]?.deviceId;
    if (!nextId) return;

    setSelectedCameraId(nextId);
    await startCamera(nextId);
  };

  /**
   * Captures a still frame from the live preview.
   */
  const handleTakePhoto = () => {
    const video = videoRef.current;
    if (!video?.videoWidth || !video?.videoHeight) return;

    try {
      if (!shutterSoundRef.current) {
        shutterSoundRef.current = new Audio('/sounds/camera.mp3');
      }
      shutterSoundRef.current.currentTime = 0;
      shutterSoundRef.current.play().catch(() => {});
    } catch {
      // ignore shutter sound failures
    }

    setFlashVisible(true);
    window.setTimeout(() => setFlashVisible(false), 160);

    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    canvas.toBlob(async (blob) => {
      if (!blob) return;

      const mimeType = blob.type || 'image/jpeg';
      const fallbackName = `snapshot-${Date.now()}.jpg`;
      const localFile = buildLocalFile(blob, fallbackName, mimeType);
      const fileName = localFile?.name || fallbackName;
      const objectUrl = URL.createObjectURL(localFile);

      const defaultNode = await getDefaultNode('PHOTO', activeBase?.key || null);
      const item = {
        ...defaultNode,
        key: generateKey(),
        type: 'PHOTO',
        title: null,
        userKey: user.key,
        user: buildUserSummary(user),
        baseKey: activeBase?.key || null,
        url: objectUrl,
        localUri: objectUrl,
        base64Data: null,
        localFile,
        tempFile: localFile,
        fileName,
        mimeType,
      };

      onContentCreated?.(item);
      stopCamera();
    }, 'image/jpeg');
  };

  /**
   * Starts video recording from the active stream.
   */
  const handleStartRecording = () => {
    if (isRecording || !mediaStreamRef.current) return;
    if (typeof MediaRecorder === 'undefined') {
      setPermissionError('Video recording is not supported in this browser.');
      return;
    }

    const stream = mediaStreamRef.current;
    let recorder;
    try {
      recorder = new MediaRecorder(stream);
    } catch (error) {
      console.error('MediaRecorder failed:', error);
      setPermissionError('Could not start video recording on this device.');
      return;
    }

    chunksRef.current = [];
    mediaRecorderRef.current = recorder;

    recorder.ondataavailable = (event) => {
      if (event.data?.size > 0) {
        chunksRef.current.push(event.data);
      }
    };

    recorder.onstop = async () => {
      const videoBlob = new Blob(chunksRef.current, { type: 'video/webm' });
      const mimeType = videoBlob.type || 'video/webm';
      const extension = mimeType.includes('mp4') ? 'mp4' : 'webm';
      const fallbackName = `recording-${Date.now()}.${extension}`;
      const localFile = buildLocalFile(videoBlob, fallbackName, mimeType);
      const fileName = localFile?.name || fallbackName;
      const uri = URL.createObjectURL(localFile);

      const defaultNode = await getDefaultNode('VIDEO', activeBase?.key || null);
      const item = {
        ...defaultNode,
        key: generateKey(),
        type: 'VIDEO',
        title: null,
        userKey: user.key,
        user: buildUserSummary(user),
        baseKey: activeBase?.key || null,
        url: uri,
        localUri: uri,
        localFile,
        tempFile: localFile,
        fileName,
        mimeType,
        base64Data: null,
      };

      setIsRecording(false);
      mediaRecorderRef.current = null;
      onContentCreated?.(item);
      stopCamera();
    };

    recorder.start();
    setIsRecording(true);
  };

  /**
   * Stops an in-progress recording (finalize in onstop).
   */
  const handleStopRecording = () => {
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== 'inactive') {
      recorder.stop();
      return;
    }
    stopCamera();
  };

  /**
   * Primary shutter action for the active mode.
   */
  const handleShutterClick = () => {
    if (mode === CAMERA_MODES.PHOTO) {
      handleTakePhoto();
      return;
    }

    if (isRecording) {
      handleStopRecording();
    } else {
      handleStartRecording();
    }
  };

  const isVideoMode = mode === CAMERA_MODES.VIDEO;
  const shutterLabel = isVideoMode
    ? (isRecording ? 'Stop recording' : 'Start recording')
    : 'Take photo';

  // ----- RENDER ----- //

  return (
    <div className="camera-container">

      {/* PREVIEW */}
      <video
        className="camera-preview"
        ref={videoRef}
        autoPlay
        playsInline
        muted
      />

      {/* FLASH */}
      {flashVisible ? <div className="camera-flash" aria-hidden="true" /> : null}

      {/* TOP BAR */}
      <div className="camera-top-bar">
        {isVideoMode && isRecording ? (
          <div className="camera-recording-badge">
            <span className="camera-recording-dot" aria-hidden="true" />
            <RecordingTimer
              isRecording={isRecording}
              onMaxDurationReached={handleStopRecording}
            />
          </div>
        ) : null}
      </div>

      {/* ERROR / STATUS */}
      {permissionError ? (
        <div className="camera-status camera-status--error">
          <div className="camera-status-message">{permissionError}</div>
          <button
            className="camera-retry-button"
            type="button"
            onClick={() => startCamera(selectedCameraId)}
          >
            Try again
          </button>
        </div>
      ) : null}

      {isStarting && !permissionError ? (
        <div className="camera-status">
          <div className="camera-status-message">Starting camera…</div>
        </div>
      ) : null}

      {/* CONTROLS */}
      <div className="camera-controls">

        {/* MODE TABS */}
        <div
          className="camera-mode-tabs"
          role="tablist"
          aria-label="Camera mode"
        >
          <button
            className={`camera-mode-tab${mode === CAMERA_MODES.PHOTO ? ' is-active' : ''}`}
            type="button"
            role="tab"
            aria-selected={mode === CAMERA_MODES.PHOTO}
            disabled={isRecording}
            onClick={() => handleModeChange(CAMERA_MODES.PHOTO)}
          >
            Photo
          </button>
          <button
            className={`camera-mode-tab${mode === CAMERA_MODES.VIDEO ? ' is-active' : ''}`}
            type="button"
            role="tab"
            aria-selected={mode === CAMERA_MODES.VIDEO}
            disabled={isRecording}
            onClick={() => handleModeChange(CAMERA_MODES.VIDEO)}
          >
            Video
          </button>
        </div>

        {/* SHUTTER */}
        <div className="camera-shutter-row">
          <div className="camera-shutter-side" aria-hidden="true" />

          <button
            className={[
              'camera-shutter',
              isVideoMode ? 'camera-shutter--video' : 'camera-shutter--photo',
              isRecording ? 'is-recording' : '',
            ].filter(Boolean).join(' ')}
            type="button"
            aria-label={shutterLabel}
            title={shutterLabel}
            disabled={Boolean(permissionError) || isStarting}
            onClick={handleShutterClick}
          >
            <span className="camera-shutter-inner">
              {isRecording ? <span className="camera-shutter-stop" /> : null}
            </span>
          </button>

          <div className="camera-shutter-side camera-shutter-side--end">
            {cameras.length > 1 ? (
              <button
                className="camera-flip-button"
                type="button"
                aria-label="Switch camera"
                title="Switch camera"
                disabled={isRecording || isStarting}
                onClick={handleFlipCamera}
              >
                <Icon
                  iconKey="cameraswitch"
                  width={22}
                  height={22}
                  color="#ffffff"
                />
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

// Dev Tools
Camera.displayName = 'Camera';

export default Camera;
